import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
} from "@certn/cascadia-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useIdleTimer } from "react-idle-timer";

import { useAuth } from "./hooks/useAuth";
import { useLogout } from "./hooks/useLogout";
import { parseJWT } from "./utils";

/*
  - When there's activity, force refresh the token (but throttled)
  - Check the token every minute.
    - (If it's null then we have 1 min before logout)
    - If it's not null then save the expiry time
    - If (expiry - now) < 30s, show modal
*/

const FAR_FUTURE = 5_000_000_000; // seconds
const ONE_DAY = 86_400; // seconds
const WARNING_PERIOD = 60; // seconds

// getAccessTokenSilently() would return undefined when token expires in less than 60 seconds
// This is probably a bug with auth0-react, but we can give token a buffer time as a fix
const TIME_BUFFER = 60; // seconds

const THROTTLE_REFRESH = 60_000; // milliseconds
const CHECK_TOKEN_PERIOD = 1_000; // milliseconds

export function SessionTimeoutMonitor() {
  const { t } = useTranslation();
  const auth = useAuth();
  const { logout } = useLogout();

  const [tokenExpiresIn, setTokenExpiresIn] = useState(ONE_DAY);

  const refreshAccessToken = () => {
    updateTokenExpiryTime("off");
  };

  const updateTokenExpiryTime = async (cacheMode: "off" | "cache-only") => {
    let expired = false;

    try {
      const token = await auth.getAccessTokenSilently({ cacheMode });

      // getAccessTokenSilently can return undefined when token is expired or cache is empty
      if (!token || typeof token !== "string") {
        expired = true;
      } else {
        const jwt = parseJWT(token);
        const tokenExpiryTime = (jwt.exp ?? FAR_FUTURE) - TIME_BUFFER;
        const nowSeconds = Date.now() / 1000;

        setTokenExpiresIn(Math.max(tokenExpiryTime - nowSeconds, 1));

        if (tokenExpiryTime <= nowSeconds) {
          expired = true;
        }
      }
    } catch (e) {
      console.error("Error updating token:", e);
      expired = true;
    }

    if (expired) {
      // Use centralized logout with federated logout support for SSO users
      await logout({ logoutType: "session_timeout" });
    }
  };

  useEffect(refreshAccessToken, []);

  useIdleTimer({
    onAction: refreshAccessToken,
    throttle: THROTTLE_REFRESH,
  });

  useEffect(() => {
    const interval = setInterval(
      () => updateTokenExpiryTime("cache-only"),
      CHECK_TOKEN_PERIOD
    );

    return () => clearInterval(interval);
  }, [auth, logout]);

  return (
    <Modal isOpen={tokenExpiresIn < WARNING_PERIOD}>
      <Dialog>
        <DialogHeader onClose={refreshAccessToken}>
          {t("Warning timeout")}
        </DialogHeader>
        <DialogContent>
          {t("You will be signed out in {{seconds}} seconds.", {
            seconds: Math.round(tokenExpiresIn),
          })}
        </DialogContent>
        <DialogFooter>
          <BasicButton size="medium" onPress={refreshAccessToken}>
            {t("Continue working")}
          </BasicButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
